import React from "react";
import { LanguageCode, pickLang } from "../../utils/translations";
import { COLORS } from "../../utils/constants";

interface WhyItMattersProps {
  lang: LanguageCode;
}

const WhyItMatters: React.FC<WhyItMattersProps> = ({ lang }) => {
  const content = {
    en: {
      heading: "Why This Matters",
      subheading:
        "These patterns aren’t random. They explain why some YouTube videos explode while others quietly disappear — regardless of creator size.",
      items: [
        {
          title: "📈 Evergreen Beats Trends",
          desc: "Many top videos stay relevant for years. Timing matters — but replay value matters more.",
        },
        {
          title: "🧠 Emotion Drives Virality",
          desc: "Music, nostalgia, humor, and surprise consistently outperform technical perfection.",
        },
        {
          title: "🚀 One Hit Can Change Everything",
          desc: "YouTube success is often non-linear. One breakout video can define an entire channel.",
        },
      ],
    },
    hi: {
      heading: "यह क्यों मायने रखता है",
      subheading:
        "ये पैटर्न रैंडम नहीं हैं। वे बताते हैं कि क्यों कुछ YouTube वीडियो वायरल हो जाते हैं जबकि अन्य चुपचाप गायब हो जाते हैं — क्रिएटर चाहे कितना भी बड़ा या छोटा क्यों न हो।",
      items: [
        {
          title: "📈 एवरग्रीन ट्रेंड्स से बेहतर है",
          desc: "कई टॉप वीडियो सालों तक प्रासंगिक बने रहते हैं। टाइमिंग मायने रखती है — लेकिन रिप्ले वैल्यू उससे भी ज़्यादा मायने रखती है।",
        },
        {
          title: "🧠 भावनाएं वाइरलिटी बढ़ाती हैं",
          desc: "संगीत, पुरानी यादें, हास्य और सरप्राइज लगातार तकनीकी पूर्णता से बेहतर प्रदर्शन करते हैं।",
        },
        {
          title: "🚀 एक हिट सब कुछ बदल सकता है",
          desc: "YouTube की सफलता अक्सर नॉन-लीनियर होती है। एक सफल वीडियो पूरे चैनल की दिशा बदल सकता है।",
        },
      ],
    },
    fr: {
      heading: "Pourquoi c'est important",
      subheading:
        "Ces schémas ne sont pas aléatoires. Ils expliquent pourquoi certaines vidéos YouTube explosent alors que d'autres disparaissent discrètement — quelle que soit la taille du créateur.",
      items: [
        {
          title: "📈 L'intemporel bat les tendances",
          desc: "De nombreuses vidéos populaires restent pertinentes pendant des années. Le timing compte — mais la valeur de replay compte encore plus.",
        },
        {
          title: "🧠 L'émotion stimule la viralité",
          desc: "La musique, la nostalgie, l'humour et la surprise surpassent systématiquement la perfection technique.",
        },
        {
          title: "🚀 Un seul succès peut tout changer",
          desc: "Le succès sur YouTube est souvent non linéaire. Une seule vidéo à succès peut définir toute une chaîne.",
        },
      ],
    },
  };

  const { heading, subheading, items } = pickLang(content, lang);

  //  const brandPurple = "#714B67";
  //  const brandTeal = "#017E84";

  return (
    <section
      style={{
        padding: "10px 8%",
        backgroundColor: "#f9fafb",
        textAlign: "center",
      }}
    >
      <h2
        style={{
          fontSize: "1.8rem",
          marginBottom: "0.1rem",
          color: COLORS.brandPurple, // UPDATED: Authoritative Brand Color
          marginTop: 0,
          fontWeight: 800,
        }}
      >
        {heading}
      </h2>

      <p
        style={{
          maxWidth: "700px",
          margin: "0 auto 1rem",
          fontSize: "0.95rem",
          color: "#4A5568", // UPDATED: Deep Slate Gray for readability
          lineHeight: "1.6",
          marginTop: 0,
        }}
      >
        {subheading}
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "20px",
          maxWidth: "1100px",
          margin: "0 auto",
        }}
      >
        {items.map((item, idx) => (
          <div
            key={idx}
            style={{
              padding: "10px",
              backgroundColor: "white",
              borderRadius: "16px",
              border: "1px solid #f0f0f0",
              boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
              textAlign: "left", // Better readability for grid items
            }}
          >
            <h4
              style={{
                fontSize: "1.05rem",
                marginBottom: "2px",
                fontWeight: "bold",
                color: COLORS.brandPurple,
              }}
            >
              {item.title}
            </h4>
            <p
              style={{
                fontSize: "0.88rem",
                color: "#4A5568",
                lineHeight: "1.5",
                margin: 0,
              }}
            >
              {item.desc}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default WhyItMatters;
